// libs
import styled, { css } from "styled-components";
import { HTMLMotionProps, motion, Variants } from "framer-motion";
// components
import Icon from "./Icon";
import { Text } from "./Text";

interface SliderButtonProps extends HTMLMotionProps<"button"> {
  direction: "prev" | "next";
  className?: string;
}

const Button = styled(motion.button)`
  background-color: ${({ theme }) => theme.colors.hotPink};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  line-height: 48px;
  height: 64px;
  width: 40px;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows[0]};

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      height: 48px;
      width: 160px;
      border-radius: 32px 32px 0 0;
    }
  `}
`;

const ButtonPosition = styled.div<{ direction: "prev" | "next" }>`
  position: absolute;
  top: 50%;
  z-index: 2;
  pointer-events: all;

  ${({ theme, direction }) =>
    direction === "prev"
      ? css`
          left: 0;

          ${Button} {
            border-radius: 0 32px 32px 0;
          }

          ${theme.mediaQueries.md} {
            transform: translateY(-50%) rotate(90deg) translateX(-50%);
            transform-origin: bottom left;
            ${Button} {
              border-radius: 32px 32px 0 0;
              padding-left: 16px;
            }
            ${IconPosition} {
              left: 16px;
              transform: translateY(-50%) rotate(-90deg);
            }
          }
        `
      : css`
          right: 0;

          ${Button} {
            border-radius: 32px 0 0 32px;
          }

          ${theme.mediaQueries.md} {
            transform: translateY(-50%) rotate(-90deg) translateX(50%);
            transform-origin: bottom right;

            ${Button} {
              border-radius: 32px 32px 0 0;
              padding-right: 16px;
            }
            ${IconPosition} {
              right: 16px;
              transform: translateY(-50%) rotate(90deg);
            }
          }
        `}
`;

const IconPosition = styled.span`
  display: inline-block;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      position: absolute;
      top: 50%;
    }
  `}

  svg {
    vertical-align: middle;
    height: 48px;
  }
`;

const Label = styled(Text)`
  display: none;
  font-weight: 700;
  /* vertical-align: middle; */

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      display: inline-block;
    }
  `}
`;

const buttonVariants: Variants = {
  hover: { scale: 1.04 },
  normal: { scale: 1 },
};

export const SliderButton: React.FC<SliderButtonProps> = ({
  direction,
  className,
  children,
  ...rest
}) => {
  return (
    <ButtonPosition direction={direction}>
      <Button
        className={className}
        {...rest}
        whileHover="hover"
        animate="normal"
        variants={buttonVariants}
      >
        <IconPosition>
          {direction === "prev" ? <Icon icon="navigateBefore" /> : null}
          {direction === "next" ? <Icon icon="navigateNext" /> : null}
        </IconPosition>
        <Label as="span">{children}</Label>
      </Button>
    </ButtonPosition>
  );
};
